/**
 * PROPER FIX: Make FormsLandingComponent Reactive to User Changes
 * 
 * This fixes the root cause of chunk errors by ensuring the component
 * properly reacts to user state changes instead of only loading once.
 */

import { Component, computed, effect } from '@angular/core';
import { Router } from '@angular/router';
import { FormModelService } from '../../../services/form-model.service';
import { FormMetadata } from '../../../models/form-registry';
import { EntraService } from '../../../services/entra.service';
import { ScreenSizeService } from '../../../services/screen-size.service';
import { FormService } from '../../../services/form.service';
import { environment } from '../../../../environments/environment';
import { User } from '../../../models/User.interface';

@Component({
  selector: 'app-forms-landing',
  templateUrl: './forms-landing.component.html',
  styleUrls: ['./forms-landing.component.css'],
})
export class FormsLandingComponent {
  // Make these computed signals so they automatically update when user changes
  // This is the KEY FIX - makes component reactive to user state changes
  
  /**
   * Current user - automatically updates when EntraService user signal changes
   */
  currentUser = computed(() => this.entraService.user());
  
  /**
   * User admin status - derived from current user
   */
  userIsAdmin = computed(() => this.currentUser()?.isAdmin ?? false);
  
  /**
   * Accessible forms - automatically recomputes when user changes
   * This ensures forms list is always in sync with user permissions
   */
  accessibleForms = computed(() => 
    this.formModelService.getAccessibleForms(this.currentUser())
  );

  constructor(
    private router: Router,
    private formService: FormService,
    private formModelService: FormModelService,
    private entraService: EntraService,
    public screenSizeService: ScreenSizeService
  ) {
    // Optional: Add effect to log user/forms state changes (helpful for debugging)
    if (!environment.production) {
      effect(() => {
        const user = this.currentUser();
        const forms = this.accessibleForms();
        console.log('FormsLandingComponent state update:', {
          user: user?.email ?? 'Not loaded',
          isAdmin: this.userIsAdmin(),
          formsCount: forms.length
        });
      });
    }
  }

  /**
   * Opens the external vehicle collision form in a new tab
   */
  public openVehicleCollisionForm(): void {
    this.formService.openExternalVehicleCollisionForm();
  }

  /**
   * Navigates to the specified form type for creating a new entry
   */
  createNew(formType: string): void {
    this.router.navigate(['/forms', formType]);
  }

  /**
   * Navigates to the form entries list filtered by the specified form type
   * Only available to admin users
   */
  viewEntries(formType: string): void {
    console.log('Navigating to form entries for form type:', formType);
    this.router.navigate(['/form-entries'], {
      queryParams: { formId: formType },
    });
  }

  /**
   * Checks if a specific form has access restrictions
   */
  formHasAccessRestrictions(formType: string): boolean {
    return this.formModelService.formHasAccessRestrictions(formType);
  }

  /**
   * Gets the access requirements for a specific form
   */
  getFormAccessRequirements(formType: string) {
    return this.formModelService.getFormAccessRequirements(formType);
  }
}